/**
 * `useSeatKnockOn` — the knock-on of a ladder seat (#1964, slice 3D of #1947).
 *
 *   * `usePreviewSeat` → `POST /api/cycles/{id}/shortfalls/seat/preview` —
 *     the seat as a review draft: the person moving from the ward they sit
 *     on to the short cell, and the one-hop backfill of the cell they leave
 *     (or that cell left short). Writes nothing.
 *   * `useApplySeat` → `POST …/shortfalls/seat/apply` — the preview's
 *     `token` + `changes` and the review screen's decisions (`skip`, `seat`);
 *     409 when either cell changed since the preview, 422 when the team's
 *     scheduling rules object (re-send with `acknowledgeRules`).
 *
 * The apply invalidates what the plain seat does — the roster, every
 * ladder read of the cycle, the Review cover inbox, Coverage — plus the team
 * roster range and the cover history it may have counted.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { ReviewDecision } from "../lib/changeReview";
import { type ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";
import { coverageReviewQueryFilter } from "./useCoverageReview";
import { cycleRosterQueryFilter } from "./useCycleRoster";
import { mcAttentionQueryKey } from "./useMcAttention";
import { shortfallOptionsQueryFilter } from "./useShortfallOptions";

export type SeatPreviewResponse = components["schemas"]["SeatPreviewResponse"];
export type SeatApplyResponse = components["schemas"]["SeatApplyResponse"];
export type KnockOnChange = components["schemas"]["KnockOnChange"];
export type KnockOnBackfill = components["schemas"]["KnockOnBackfill"];

/** The seat the lead picked — the plain seat route's body. */
export interface SeatTarget {
  date: string;
  station: string;
  session: string;
  membershipId: string;
  floorGroup?: string | null | undefined;
  actingUp?: boolean | undefined;
}

function seatBody(t: SeatTarget): Record<string, unknown> {
  return {
    date: t.date,
    station: t.station,
    session: t.session,
    membership_id: t.membershipId,
    floor_group: t.floorGroup ?? null,
    ...(t.actingUp ? { acting_up: true } : {}),
  };
}

export function usePreviewSeat(cycleId: string) {
  return useMutation<SeatPreviewResponse, ApiError | Error, SeatTarget>({
    mutationFn: (t) =>
      apiFetch<SeatPreviewResponse>(`/api/cycles/${cycleId}/shortfalls/seat/preview`, {
        method: "POST",
        body: JSON.stringify(seatBody(t)),
      }),
  });
}

export interface ApplySeatVars {
  target: SeatTarget;
  preview: SeatPreviewResponse;
  decision: Pick<ReviewDecision, "skip" | "seat">;
  acknowledgeRules?: boolean | undefined;
}

export function useApplySeat(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<SeatApplyResponse, ApiError | Error, ApplySeatVars>({
    mutationFn: ({ target, preview, decision, acknowledgeRules }) =>
      apiFetch<SeatApplyResponse>(`/api/cycles/${cycleId}/shortfalls/seat/apply`, {
        method: "POST",
        body: JSON.stringify({
          ...seatBody(target),
          confirm_save_with_thresholds_outside: acknowledgeRules ?? false,
          token: preview.token,
          changes: preview.changes,
          skip: decision.skip,
          seat: decision.seat,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries(cycleRosterQueryFilter(cycleId));
      qc.invalidateQueries(shortfallOptionsQueryFilter(cycleId));
      qc.invalidateQueries({ queryKey: mcAttentionQueryKey(cycleId) });
      qc.invalidateQueries(coverageReviewQueryFilter(cycleId));
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "team-range-roster" });
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "team-cover-history" });
    },
  });
}
